import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import type { RenewalScheduleItem } from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { incrementInvoiceScheduleLine } from '../../services/invoiceScheduleAdminService';
import './reconciliationPanel.css';

interface Props {
  open: boolean;
  item: RenewalScheduleItem;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Alter the billed quantity on a recurring invoice schedule line — the billing
 * realization. Updates RepairShopr (which drives the next invoice run) AND the
 * Dataverse mirror via the existing increment endpoint; we send the delta to
 * reach the new quantity. Sibling of the subscription (commitment) edit so both
 * sides of a seat change can be maintained from the customer drill-down.
 */
export const ScheduleLineEditDialog: React.FC<Props> = ({ open, item, onClose, onSaved }) => {
  const [quantity, setQuantity] = useState<number>(item.quantity);
  const [description, setDescription] = useState<string>(item.productName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const delta = Math.round(quantity) - item.quantity;
  const descriptionChanged = description !== (item.productName ?? '');

  const submit = async () => {
    if (!Number.isFinite(quantity) || quantity < 0) {
      setError('Quantity must be a non-negative number.');
      return;
    }
    if (delta === 0 && !descriptionChanged) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await incrementInvoiceScheduleLine(item.scheduleId, {
        productSku: item.productSku,
        ...(delta !== 0 ? { delta } : {}),
        ...(descriptionChanged ? { description } : {}),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update schedule line');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-label="Edit billed quantity"
      PaperProps={{
        sx: { background: 'transparent', boxShadow: 'none', borderRadius: 0, overflow: 'visible' },
      }}
    >
      <SidePanel
        title="Edit billed quantity"
        submitLabel={saving ? 'Saving…' : 'Save'}
        isValid={!saving && Number.isFinite(quantity) && quantity >= 0}
        onClose={() => {
          if (!saving) onClose();
        }}
        onSubmit={submit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        <p className="recon-dialog__hint">
          {item.productSku} — updates the recurring invoice in RepairShopr (drives the
          next run) and SM4RT.
        </p>

        <FormField label="Billed quantity">
          <input
            type="number"
            min={0}
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
          />
        </FormField>
        <span className="recon-dialog__help">
          {delta !== 0 ? `Change of ${delta > 0 ? '+' : ''}${delta}` : `Current: ${item.quantity}`}
        </span>

        <FormField label="Description">
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>
        <span className="recon-dialog__help">The line text shown on the invoice.</span>
      </SidePanel>
    </Dialog>
  );
};
